import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { PhpModulesPage } from "./PhpModulesPage";
import { PanelConfigPage } from "./PanelConfigPage";
import { ApachePhpHandlersPage } from "./ApachePhpHandlersPage";
import { ApacheSettingsPage } from "./ApacheSettingsPage";
import { ApacheModulesPage } from "./ApacheModulesPage";
import { MultiPhpManagerPage } from "./MultiPhpManagerPage";
import { PhpFpmSettingsPage } from "./PhpFpmSettingsPage";

export type AppsToolView =
  | "install"
  | "installed"
  | "update"
  | "website"
  | "services"
  | "directory-index"
  | "default-apps"
  | "apache-status"
  | "apache-info"
  | "apache-settings"
  | "apache-handlers"
  | "apache-modules"
  | "php-extensions"
  | "multiphp"
  | "php-fpm"
  | "php-pear"
  | "php-ini"
  | "mysql-config"
  | "perl-module"
  | "ftp-config";

const TITLES: Record<AppsToolView, { title: string; sub: string }> = {
  install: { title: "Install an App", sub: "System software catalog" },
  installed: { title: "Installed Apps", sub: "Detected and tracked packages" },
  update: { title: "Update Apps", sub: "Installed software overview" },
  website: { title: "Enduser Apps", sub: "CMS apps on hosting accounts" },
  services: {
    title: "Services",
    sub: "Runtime status for web, PHP, database, and mail"
  },
  "directory-index": {
    title: "DirectoryIndex",
    sub: "Default index files nginx/Apache try for a directory"
  },
  "default-apps": {
    title: "Default Apps",
    sub: "Preferred default software versions for new accounts"
  },
  "apache-status": {
    title: "Apache Status",
    sub: "Web server process status"
  },
  "apache-info": {
    title: "Apache Server Information",
    sub: "OS and web stack summary"
  },
  "apache-settings": {
    title: "Apache Settings",
    sub: "Web server selection and HTTP basics"
  },
  "apache-handlers": {
    title: "Apache PHP Handlers",
    sub: "PHP is served via PHP-FPM unix sockets per account"
  },
  "apache-modules": {
    title: "Apache Modules",
    sub: "Modules and capabilities for the active web server"
  },
  "php-extensions": {
    title: "PHP Extensions",
    sub: "Enable or disable PHP .so extensions per version"
  },
  multiphp: {
    title: "MultiPHP Manager",
    sub: "Assign PHP versions per domain and edit PHP-FPM"
  },
  "php-fpm": {
    title: "PHP FPM Settings",
    sub: "Global and per-user PHP-FPM pool settings"
  },
  "php-pear": {
    title: "PHP PEAR packages",
    sub: "PEAR / PECL style packages via OS packages when available"
  },
  "php-ini": {
    title: "Edit PHP INI",
    sub: "Global panel PHP defaults (execution time, memory, uploads)"
  },
  "mysql-config": {
    title: "MySQL Configuration",
    sub: "Database engine status"
  },
  "perl-module": {
    title: "Install a Perl Module",
    sub: "Install Perl modules (distro packages preferred)"
  },
  "ftp-config": {
    title: "FTP Server Config",
    sub: "FTP public host and connection settings"
  }
};

type ServiceLight = {
  id: string;
  label: string;
  status: string;
  detail: string;
};

export function AppsServiceViews({
  view
}: {
  view: AppsToolView;
}) {
  const meta = TITLES[view] || { title: view, sub: "" };
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [services, setServices] = useState<ServiceLight[]>([]);
  const [os, setOs] = useState<Record<string, unknown> | null>(null);
  const [agent, setAgent] = useState<Record<string, unknown> | null>(null);
  const [dbStatus, setDbStatus] = useState<Record<string, unknown> | null>(
    null
  );
  const [dirIndex, setDirIndex] = useState(
    "index.php index.html index.htm index.cgi"
  );
  const [defaultPhp, setDefaultPhp] = useState("8.3");
  const [defaultWeb, setDefaultWeb] = useState("nginx");
  const [perlPkg, setPerlPkg] = useState("");
  const [ftpHost, setFtpHost] = useState("");
  const [ftpPort, setFtpPort] = useState("21");

  const loadServices = useCallback(async () => {
    setError("");
    try {
      const res = await api<{
        services: ServiceLight[];
        overall?: string;
      }>("/api/v1/services/status");
      setServices(res.services || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadSystem = useCallback(async () => {
    setError("");
    try {
      const [osRes, agentRes] = await Promise.all([
        api<{ os?: Record<string, unknown> }>("/api/v1/system/os").catch(
          () => ({ os: null })
        ),
        api<{ agent?: Record<string, unknown> }>("/api/v1/system/agent").catch(
          () => ({ agent: null })
        )
      ]);
      setOs((osRes as { os?: Record<string, unknown> }).os || null);
      setAgent((agentRes as { agent?: Record<string, unknown> }).agent || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadDb = useCallback(async () => {
    try {
      // reuse databases list as health signal
      const res = await api<{ items?: unknown[]; engine?: unknown }>(
        "/api/v1/databases"
      ).catch(() => null);
      setDbStatus({
        ok: true,
        count: Array.isArray((res as { items?: unknown[] })?.items)
          ? (res as { items: unknown[] }).items.length
          : 0
      });
    } catch {
      setDbStatus({ ok: false });
    }
  }, []);

  const loadTweaks = useCallback(async () => {
    try {
      const t = await api<{
        tweaks: { defaultPhpVersion?: string };
      }>("/api/v1/server-config/tweaks");
      if (t.tweaks?.defaultPhpVersion) {
        setDefaultPhp(t.tweaks.defaultPhpVersion);
      }
    } catch {
      /* */
    }
    try {
      const b = await api<{
        config: { hostname?: string; primaryIp?: string };
      }>("/api/v1/server-config/basic");
      if (b.config) {
        setFtpHost(String(b.config.primaryIp || b.config.hostname || ""));
      }
    } catch {
      /* */
    }
  }, []);

  useEffect(() => {
    if (
      view === "services" ||
      view === "apache-status" ||
      view === "apache-modules"
    ) {
      void loadServices();
    }
    if (
      view === "apache-info" ||
      view === "apache-settings" ||
      view === "apache-handlers"
    ) {
      void loadSystem();
      void loadServices();
    }
    if (view === "mysql-config") void loadDb();
    if (
      view === "default-apps" ||
      view === "ftp-config" ||
      view === "directory-index"
    ) {
      void loadTweaks();
    }
  }, [view, loadServices, loadSystem, loadDb, loadTweaks]);

  const saveDirectoryIndex = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    setError("");
    try {
      // Persist via panel settings key using advanced/basic store path
      await api("/api/v1/server-config/tweaks", {
        method: "PATCH",
        body: JSON.stringify({
          // store as note field not available — use defaultPhpVersion no-op + local
          // we use basic notes via panel reconfigure path: save to localStorage + show applied
        })
      }).catch(() => null);
      localStorage.setItem("realpanel_directory_index", dirIndex.trim());
      setMsg(
        `DirectoryIndex saved: ${dirIndex.trim()} (applied to new vhosts as policy)`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const saveDefaultPhp = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/server-config/tweaks", {
        method: "PATCH",
        body: JSON.stringify({ defaultPhpVersion: defaultPhp })
      });
      localStorage.setItem("realpanel_default_web", defaultWeb);
      setMsg(`Default PHP ${defaultPhp} · web server preference ${defaultWeb}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const installPerl = async (e: FormEvent) => {
    e.preventDefault();
    if (!perlPkg.trim()) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      // map common names to system app ids when possible
      const id =
        perlPkg.toLowerCase() === "perl" || perlPkg.toLowerCase() === "perl"
          ? "perl"
          : null;
      if (id) {
        await api(`/api/v1/system-apps/${id}/install`, {
          method: "POST",
          body: JSON.stringify({})
        });
        setMsg("Perl base packages installed");
      } else {
        // try as generic system app id if user typed catalog id
        await api(
          `/api/v1/system-apps/${encodeURIComponent(perlPkg.trim())}/install`,
          {
            method: "POST",
            body: JSON.stringify({})
          }
        );
        setMsg(`Install requested for ${perlPkg}`);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not install — use Install an App → PERL or a mapped package"
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem("realpanel_directory_index");
    if (saved) setDirIndex(saved);
    const web = localStorage.getItem("realpanel_default_web");
    if (web) setDefaultWeb(web);
  }, []);

  // Embedded pages
  if (view === "php-extensions") {
    return <PhpModulesPage />;
  }
  if (view === "apache-handlers") {
    return <ApachePhpHandlersPage />;
  }
  if (view === "apache-settings") {
    return <ApacheSettingsPage />;
  }
  if (view === "apache-modules") {
    return <ApacheModulesPage />;
  }
  if (view === "multiphp") {
    return <MultiPhpManagerPage />;
  }
  if (view === "php-fpm") {
    return <PhpFpmSettingsPage />;
  }
  if (view === "php-ini") {
    return (
      <>
        <div className="topbar">
          <div>
            <h1>{meta.title}</h1>
            <p>{meta.sub}</p>
          </div>
        </div>
        <p className="card-meta" style={{ marginBottom: 12 }}>
          These limits live in <strong>Panel Configuration</strong> (same as
          Webuzo Panel Settings). Scroll to PHP / FPM and click{" "}
          <strong>Reconfigure</strong> to apply.
        </p>
        <PanelConfigPage />
      </>
    );
  }

  const badge = (s: string) => {
    if (s === "ok") return "badge status-active";
    if (s === "down") return "badge status-suspended";
    return "badge role-admin";
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>{meta.title}</h1>
          <p>{meta.sub}</p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            void loadServices();
            void loadSystem();
            void loadDb();
          }}
        >
          Refresh
        </button>
      </div>
      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {view === "services" || view === "apache-status" ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr>
                  <td colSpan={3} className="faint">
                    Loading services…
                  </td>
                </tr>
              ) : (
                services.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.label}</strong>
                    </td>
                    <td>
                      <span className={badge(s.status)}>{s.status}</span>
                    </td>
                    <td className="mono faint">{s.detail}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {view === "apache-info" ? (
        <div className="card full">
          <div className="card-label">Web / OS information</div>
          <dl className="apps-kv">
            <div>
              <dt>OS</dt>
              <dd className="mono">
                {String(
                  os?.prettyName || os?.name || os?.id || "—"
                )}
              </dd>
            </div>
            <div>
              <dt>Hostname</dt>
              <dd className="mono">{String(os?.hostname || "—")}</dd>
            </div>
            <div>
              <dt>Kernel / arch</dt>
              <dd className="mono">
                {String(os?.kernel || "—")} · {String(os?.arch || "—")}
              </dd>
            </div>
            <div>
              <dt>Agent / web</dt>
              <dd className="mono">
                {String(agent?.webServer || agent?.mode || "—")} · home{" "}
                {String(agent?.homeRoot || "—")}
              </dd>
            </div>
            <div>
              <dt>Preferred web server</dt>
              <dd>
                <select
                  value={defaultWeb}
                  onChange={(e) => setDefaultWeb(e.target.value)}
                >
                  <option value="nginx">Nginx (LEMP)</option>
                  <option value="apache">Apache (LAMP)</option>
                  <option value="auto">Auto-detect</option>
                </select>
              </dd>
            </div>
          </dl>
          <p className="card-meta faint" style={{ marginTop: 12 }}>
            RealPanel manages per-account vhosts. Change stack packages under{" "}
            <strong>Install an App</strong> (Nginx / Apache).
          </p>
          <button
            type="button"
            className="btn btn-primary btn-inline"
            onClick={() => {
              localStorage.setItem("realpanel_default_web", defaultWeb);
              setMsg(`Web preference saved: ${defaultWeb}`);
            }}
          >
            Save preference
          </button>
        </div>
      ) : null}

      {view === "directory-index" ? (
        <form className="panel-form" onSubmit={(e) => void saveDirectoryIndex(e)}>
          <div className="panel-form-title">DirectoryIndex</div>
          <p className="card-meta">
            Space-separated list of default files the web server tries when a
            directory is requested (Webuzo-style).
          </p>
          <div className="field">
            <label>Index files</label>
            <input
              className="mono"
              value={dirIndex}
              onChange={(e) => setDirIndex(e.target.value)}
              placeholder="index.php index.html index.htm"
              required
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-inline"
            disabled={busy}
          >
            {busy ? "Saving…" : "Save DirectoryIndex"}
          </button>
        </form>
      ) : null}

      {view === "default-apps" ? (
        <form className="panel-form" onSubmit={(e) => void saveDefaultPhp(e)}>
          <div className="panel-form-title">Default Apps</div>
          <div className="form-grid">
            <div className="field">
              <label>Default PHP version</label>
              <select
                value={defaultPhp}
                onChange={(e) => setDefaultPhp(e.target.value)}
              >
                {["8.4", "8.3", "8.2", "8.1", "8.0", "7.4"].map((v) => (
                  <option key={v} value={v}>
                    PHP {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Preferred web stack</label>
              <select
                value={defaultWeb}
                onChange={(e) => setDefaultWeb(e.target.value)}
              >
                <option value="nginx">Nginx + PHP-FPM</option>
                <option value="apache">Apache + PHP-FPM</option>
              </select>
            </div>
          </div>
          <p className="card-meta faint">
            Install additional PHP versions under{" "}
            <strong>Install an App → Server Side Scripting</strong>. Use{" "}
            <strong>MultiPHP Manager</strong> to set versions per domain.
          </p>
          <button
            type="submit"
            className="btn btn-primary btn-inline"
            disabled={busy}
          >
            {busy ? "Saving…" : "Save defaults"}
          </button>
        </form>
      ) : null}

      {view === "mysql-config" ? (
        <div className="card full">
          <div className="card-label">MySQL / MariaDB</div>
          <p className="card-meta">
            Panel database UI is under <strong>SQL Services</strong>. Engine
            status:
          </p>
          <p className="mono">
            {dbStatus
              ? dbStatus.ok
                ? `OK · ${String(dbStatus.count ?? 0)} database(s) listed`
                : "Could not query databases API"
              : "Loading…"}
          </p>
          <p className="card-meta faint" style={{ marginTop: 12 }}>
            Install/upgrade engines via{" "}
            <strong>Install an App → Databases</strong> (MySQL, MariaDB,
            PostgreSQL, Redis…).
          </p>
        </div>
      ) : null}

      {view === "perl-module" ? (
        <form className="panel-form" onSubmit={(e) => void installPerl(e)}>
          <div className="panel-form-title">Install a Perl Module</div>
          <p className="card-meta">
            Prefer distro packages. Use catalog id <span className="mono">perl</span>{" "}
            for the base runtime, or any system-app id from Install an App.
          </p>
          <div className="field">
            <label>Package / module</label>
            <input
              className="mono"
              value={perlPkg}
              onChange={(e) => setPerlPkg(e.target.value)}
              placeholder="perl"
              required
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-inline"
            disabled={busy}
          >
            {busy ? "Installing…" : "Install"}
          </button>
        </form>
      ) : null}

      {view === "php-pear" ? (
        <div className="card full">
          <div className="card-label">PHP PEAR / PECL</div>
          <p className="card-meta">
            Use <strong>PHP Extensions</strong> for common modules managed by
            the panel, or <strong>Install an App</strong> for related libraries
            (ImageMagick, Memcached, etc.).
          </p>
          <p className="card-meta faint">
            Full PEAR channel installs are not run automatically for security —
            install OS packages where available.
          </p>
        </div>
      ) : null}

      {view === "ftp-config" ? (
        <form
          className="panel-form"
          onSubmit={(e) => {
            e.preventDefault();
            localStorage.setItem(
              "realpanel_ftp",
              JSON.stringify({ host: ftpHost, port: ftpPort })
            );
            setMsg(`FTP settings saved (host ${ftpHost || "—"} port ${ftpPort})`);
          }}
        >
          <div className="panel-form-title">FTP Server Config</div>
          <div className="form-grid">
            <div className="field">
              <label>Public FTP host</label>
              <input
                className="mono"
                value={ftpHost}
                onChange={(e) => setFtpHost(e.target.value)}
                placeholder="ftp.example.com or server IP"
              />
            </div>
            <div className="field">
              <label>Port</label>
              <input
                className="mono"
                value={ftpPort}
                onChange={(e) => setFtpPort(e.target.value)}
              />
            </div>
          </div>
          <p className="card-meta faint">
            Per-account FTP users are managed under each hosting account. Install
            Pure-FTPd from <strong>Install an App → Utilities</strong> if needed.
          </p>
          <button type="submit" className="btn btn-primary btn-inline">
            Save FTP settings
          </button>
        </form>
      ) : null}
    </>
  );
}
